import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useTouristRoutes, TouristRoute } from "@/hooks/useTouristRoutes";
import { Camera, MapPin, Clock, TrendingUp, Star, Bookmark, Loader2, Navigation, Image as ImageIcon } from "lucide-react";
import { useState } from "react";
import { logger } from "@/lib/logger";

interface TouristRoutesSheetProps {
  isOpen: boolean;
  onClose: () => void;
  userLocation: { lat: number; lng: number } | null;
  onRouteSelect: (route: TouristRoute) => void;
}

export function TouristRoutesSheet({ 
  isOpen, 
  onClose, 
  userLocation, 
  onRouteSelect 
}: TouristRoutesSheetProps) {
  const { routes, loading, fetchTouristRoutes, saveRoute } = useTouristRoutes();
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [selectedDistance, setSelectedDistance] = useState<number>(5);

  const handleGenerateRoutes = async () => {
    if (!userLocation) {
      logger.warn('[TouristRoutesSheet] No user location available');
      return;
    }

    setLoadingGenerate(true);
    try {
      await fetchTouristRoutes(
        userLocation.lat,
        userLocation.lng,
        selectedDistance,
        true
      );
    } finally {
      setLoadingGenerate(false);
    }
  };

  const handleSelectRoute = (route: TouristRoute) => {
    logger.info('[TouristRoutesSheet] Route selected', { routeId: route.id });
    onRouteSelect(route);
    onClose();
  };

  const handleSaveRoute = async (routeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await saveRoute(routeId);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-500/10 text-green-600 border-green-500/20';
      case 'medium': return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
      case 'hard': return 'bg-red-500/10 text-red-600 border-red-500/20';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getDifficultyLabel = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'Fácil';
      case 'medium': return 'Médio';
      case 'hard': return 'Difícil';
      default: return difficulty;
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="h-[85vh] sm:h-[90vh]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-primary" />
            Rotas Turísticas
          </SheetTitle>
          <SheetDescription>
            Explore rotas de corrida que passam por pontos turísticos e lugares interessantes
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-6">
          {/* Distance Selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Distância desejada</label>
            <div className="flex gap-2">
              {[3, 5, 7, 10].map((dist) => (
                <Button
                  key={dist}
                  variant={selectedDistance === dist ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedDistance(dist)}
                >
                  {dist} km
                </Button>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <Button 
            onClick={handleGenerateRoutes} 
            disabled={!userLocation || loadingGenerate}
            className="w-full"
          >
            {loadingGenerate ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Gerando rotas turísticas...
              </>
            ) : (
              <>
                <Navigation className="mr-2 h-4 w-4" />
                Gerar Rotas Turísticas
              </>
            )}
          </Button>

          {/* Routes List */}
          <ScrollArea className="h-[calc(85vh-280px)] sm:h-[calc(90vh-280px)]">
            {loading && routes.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : routes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Camera className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  Nenhuma rota turística gerada ainda.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Clique em "Gerar Rotas Turísticas" para começar!
                </p>
              </div>
            ) : (
              <div className="space-y-3 pb-4">
                {routes.map((route) => (
                  <Card 
                    key={route.id} 
                    className="overflow-hidden cursor-pointer hover:shadow-lg transition-all hover:scale-[1.01]"
                    onClick={() => handleSelectRoute(route)}
                  >
                    <CardContent className="p-0">
                      {/* Hero Image Section with gradient based on difficulty */}
                      <div 
                        className="relative h-40 flex items-center justify-center overflow-hidden"
                        style={{
                          background: route.difficulty === 'easy' 
                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                            : route.difficulty === 'medium'
                            ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                        }}
                      >
                        {/* Overlay pattern */}
                        <div className="absolute inset-0 opacity-10">
                          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                            <pattern id={`pattern-${route.id}`} x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                              <circle cx="20" cy="20" r="2" fill="white"/>
                            </pattern>
                            <rect width="100%" height="100%" fill={`url(#pattern-${route.id})`} />
                          </svg>
                        </div>
                        
                        {/* Icon overlay */}
                        <Camera className="w-16 h-16 text-white/40 relative z-10" />
                        
                        {/* Scenic rating badge */}
                        {route.scenic_rating && (
                          <div className="absolute top-3 right-3 bg-background/95 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                            <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                            <span className="text-sm font-semibold">{route.scenic_rating.toFixed(1)}</span>
                          </div>
                        )}
                        
                        {/* City badge */}
                        <div className="absolute bottom-3 left-3 bg-background/95 backdrop-blur-sm px-3 py-1 rounded-full">
                          <span className="text-xs font-medium">{route.city}</span>
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="p-4 space-y-3">
                        {/* Header */}
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="border-primary/30">
                                <Camera className="w-3 h-3 mr-1" />
                                Turística
                              </Badge>
                              <Badge 
                                variant="outline" 
                                className={getDifficultyColor(route.difficulty)}
                              >
                                {getDifficultyLabel(route.difficulty)}
                              </Badge>
                            </div>
                            <h3 className="font-semibold text-lg">{route.name}</h3>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => handleSaveRoute(route.id, e)}
                            className="shrink-0"
                          >
                            <Bookmark className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Description */}
                        <p className="text-sm text-muted-foreground">{route.description}</p>

                        {/* Metrics */}
                        <div className="flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4 text-primary" />
                            <span>{route.distance_km} km</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-primary" />
                            <span>{route.estimated_time_min} min</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-500" />
                            <span>{route.scenic_rating}/10</span>
                          </div>
                        </div>

                        {/* Tourist Spot Photos */}
                        {route.tourist_spots && route.tourist_spots.some((spot: any) => spot.photos?.length > 0) && (
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-muted-foreground">Fotos dos Pontos:</p>
                            <ScrollArea className="w-full whitespace-nowrap rounded-lg">
                              <div className="flex gap-2 pb-2">
                                {route.tourist_spots
                                  .filter((spot: any) => spot.photos?.length > 0)
                                  .slice(0, 5)
                                  .map((spot: any, spotIdx: number) => (
                                    <div key={spotIdx} className="flex-shrink-0">
                                      <div className="relative w-[120px] h-[90px] rounded-md overflow-hidden group">
                                        <img 
                                          src={spot.photos[0].url} 
                                          alt={spot.name}
                                          className="w-full h-full object-cover transition-transform group-hover:scale-110"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                        <div className="absolute bottom-1 left-1 right-1">
                                          <p className="text-white text-[10px] font-medium line-clamp-2">
                                            {spot.name}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                              <ScrollBar orientation="horizontal" />
                            </ScrollArea>
                          </div>
                        )}

                        {/* Tourist Spots */}
                        {route.tourist_spots && route.tourist_spots.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-muted-foreground">Pontos Turísticos:</p>
                            <div className="flex flex-wrap gap-1">
                              {route.tourist_spots.slice(0, 3).map((spot: any, idx: number) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {typeof spot === 'string' ? spot : spot.name}
                                </Badge>
                              ))}
                              {route.tourist_spots.length > 3 && (
                                <Badge variant="secondary" className="text-xs">
                                  +{route.tourist_spots.length - 3}
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Highlights */}
                        {route.highlights && route.highlights.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-muted-foreground">Destaques:</p>
                            <ul className="text-xs text-muted-foreground space-y-1">
                              {route.highlights.slice(0, 2).map((highlight, idx) => (
                                <li key={idx} className="flex items-start gap-1">
                                  <TrendingUp className="w-3 h-3 shrink-0 mt-0.5 text-primary" />
                                  <span>{highlight}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
}
